import { cva, type VariantProps } from 'class-variance-authority';
import { createLucideIcon, XIcon, type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

/**
 * `tag-plus` — a Lucide icon that ships in a newer Lucide release than the one
 * this app pins (`lucide-react@0.577`, which only has `tag` / `tags`). Built
 * with Lucide's own factory from Lucide's own icon node, so it is a real
 * `LucideIcon`: same 24×24 grid, same `currentColor` stroke, same props
 * (`size`, `strokeWidth`, `className`) as every other icon in the app.
 *
 * Delete this and import `TagPlus` from `lucide-react` once the dependency is
 * upgraded past the release that adds it.
 */
const TagPlusIcon: LucideIcon = createLucideIcon('tag-plus', [
    ['path', { d: 'M16 13h6', key: 'tag-plus-bar-h' }],
    [
        'path',
        {
            d: 'm16.5 6.5-3.914-3.914A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l1.79-1.79',
            key: 'tag-plus-body',
        },
    ],
    ['path', { d: 'M19 10v6', key: 'tag-plus-bar-v' }],
    ['circle', { cx: '7.5', cy: '7.5', r: '.5', fill: 'currentColor', key: 'tag-plus-dot' }],
]);

const tagVariants = cva(
    'inline-flex min-w-0 max-w-full items-center gap-1 text-xs font-medium px-2 py-0-5 rounded-md leading-relaxed cursor-default border border-transparent group/tag',
    {
        variants: {
            variant: {
                blue: 'bg-(--tag-blue-bg) text-(--tag-blue-color) border-(--tag-blue-border)',
                green: 'bg-(--tag-green-bg) text-(--tag-green-color) border-(--tag-green-border)',
                purple: 'bg-(--tag-purple-bg) text-(--tag-purple-color) border-(--tag-purple-border)',
                orange: 'bg-(--tag-orange-bg) text-(--tag-orange-color) border-(--tag-orange-border)',
                red: 'bg-(--tag-red-bg) text-(--tag-red-color) border-(--tag-red-border)',
                teal: 'bg-(--tag-teal-bg) text-(--tag-teal-color) border-(--tag-teal-border)',
            },
        },
        defaultVariants: {
            variant: 'blue',
        },
    },
);

export interface TagColor {
    readonly fg?: string | null;
    readonly bg?: string | null;
}

function Tag({
    className,
    variant,
    color,
    style,
    ...props
}: Omit<React.ComponentProps<'span'>, 'color'> &
    VariantProps<typeof tagVariants> & {
        /** Custom color (overrides `variant`), applied via CSS-variable utilities. */
        readonly color?: TagColor | null;
    }) {
    // A tag saved without a colour carries `{}` (the column's default). Treating
    // that as a custom colour left both variables unset, so the tag fell back to
    // a hard `currentColor` outline instead of the default variant.
    const custom = color?.fg || color?.bg ? color : null;
    return (
        <span
            data-slot="tag"
            className={cn(
                tagVariants({ variant }),
                custom && 'border-(--tag-fg) bg-(--tag-bg) text-(--tag-fg)',
                className,
            )}
            style={
                custom
                    ? ({
                          '--tag-fg': custom.fg ?? undefined,
                          '--tag-bg': custom.bg ?? undefined,
                          ...style,
                      } as React.CSSProperties)
                    : style
            }
            {...props}
        />
    );
}

function TagClose({ className, ...props }: React.ComponentProps<'button'>) {
    return (
        <button
            type="button"
            data-slot="tag-close"
            className={cn(
                'items-center justify-center size-3.5 border-none bg-transparent text-current cursor-pointer rounded-sm p-0 text-xs leading-none ml-0.5 opacity-60 transition-opacity hover:opacity-100 group-hover/tag:inline-flex',
                className,
            )}
            {...props}
        >
            <XIcon className="size-3" />
        </button>
    );
}

export { Tag, TagClose, TagPlusIcon, tagVariants };
